import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button } from "../../../../ui/components/Button";
import { Checkbox } from "../../../../ui/components/Checkbox";
import * as checkboxStyles from "../../../../ui/components/Checkbox/Checkbox.style.css";
import { Card } from "../../../../ui/components/Card";
import { Callout } from "../../../../ui/components/Callout";
import { AuthBrand } from "../../../../ui/components/AuthBrand";
import { EmailInput, PasswordInput, TextInput } from "../../../../ui/components/Form";
import { PasswordStrengthBar, pwScore } from "../../../../ui/components/Form/PasswordStrength";
import * as authText from "../../../../ui/components/Auth/authTypography.style.css";
import { useAuth } from "../../../lib/auth/hooks";
import * as authApi from "../../../lib/api/auth";
import { ApiClientError } from "../../../lib/api/client";
import {
  startSignupOnboarding,
  clearSignupOnboarding,
  getPersistedRegisterStep,
  persistRegisterStep,
  isSignupOnboarding,
} from "../../../lib/auth/signup-onboarding";
import { EmailCodeEntry } from "../../auth/EmailCodeEntry";
import * as userApi from "../../../lib/api/users";
import * as productAccessApi from "../../../lib/api/product-access";
import {
  getInviteToken,
  persistInviteContext,
} from "../../../lib/auth/invite-pending";
import { useI18n } from "../../../lib/i18n";
import type { SmartLinkAccountExistsResponse } from "../../../lib/api/types";

function PwChecklist({ pw }: { pw: string }) {
  const { t, tx } = useI18n();
  const checks = [
    { labelKey: "account.register.pwCheck8Chars", ok: pw.length >= 8 },
    { labelKey: "account.register.pwCheckUppercase", ok: /[A-Z]/.test(pw) },
    { labelKey: "account.register.pwCheckNumber", ok: /[0-9]/.test(pw) },
    { labelKey: "account.register.pwCheckSpecial", ok: /[^A-Za-z0-9]/.test(pw) },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {checks.map((c) => (
        <div key={c.labelKey} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          <span style={{ color: c.ok ? "#057A55" : undefined }} className={c.ok ? undefined : authText.checklistMark}>
            {c.ok ? "✓" : "○"}
          </span>
          <span className={authText.checklistItem} style={{ color: c.ok ? "#057A55" : undefined }}>
            {tx(c.labelKey)}
          </span>
        </div>
      ))}
    </div>
  );
}

export interface RegisterPageProps {
  email?: string;
}

export function RegisterPage({ email: emailFromSearch }: RegisterPageProps) {
  const navigate = useNavigate();
  const search = { email: emailFromSearch };
  const { register, accessToken } = useAuth();
  const { t, tx } = useI18n();
  const [step, setStep] = React.useState(() => getPersistedRegisterStep() ?? 1);
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [hasPendingInvite, setHasPendingInvite] = React.useState(false);
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [terms, setTerms] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [resendLoading, setResendLoading] = React.useState(false);
  const [smartLinkAccountExists, setSmartLinkAccountExists] =
    React.useState<SmartLinkAccountExistsResponse | null>(null);

  const goToStep = React.useCallback((n: number) => {
    const nextStep = Math.min(2, Math.max(1, n));
    setStep(nextStep);
    persistRegisterStep(nextStep);
    setError(null);
  }, []);

  React.useEffect(() => {
    const persisted = getPersistedRegisterStep();
    if (persisted != null && persisted >= 2 && isSignupOnboarding()) {
      setStep(persisted);
    }
  }, []);

  React.useEffect(() => {
    const fromSearch = search.email?.trim();
    const token = getInviteToken();
    setHasPendingInvite(Boolean(token));
    if (fromSearch) {
      setEmail(fromSearch);
      return;
    }
    if (!token) return;
    void userApi
      .fetchInvitePreview(token)
      .then((preview) => {
        if (preview.valid && preview.email) {
          persistInviteContext(token, preview.email);
          setEmail(preview.email);
          setHasPendingInvite(true);
          return;
        }
        return productAccessApi.fetchProductInvitePreview(token).then((p) => {
          if (p.valid && p.email) {
            persistInviteContext(token, p.email);
            setEmail(p.email);
            setHasPendingInvite(true);
          }
        });
      })
      .catch(() =>
        productAccessApi.fetchProductInvitePreview(token).then((p) => {
          if (p.valid && p.email) {
            persistInviteContext(token, p.email);
            setEmail(p.email);
            setHasPendingInvite(true);
          }
        }),
      );
  }, [search.email]);

  const handleAccountSubmit = async () => {
    setError(null);
    if (!terms) {
      setError(t("account.register.errorTermsRequired"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("account.register.errorPasswordsMismatch"));
      return;
    }
    if (pwScore(password) < 2) {
      setError(t("account.register.errorWeakPassword"));
      return;
    }
    setLoading(true);
    startSignupOnboarding();
    const result = await register(email, password, firstName, lastName);
    setLoading(false);
    if (result.success) {
      if (result.emailVerified) {
        clearSignupOnboarding();
        navigate({
          to: "/user-setup",
          search: { tab: "phone" },
        });
      } else {
        goToStep(2);
      }
    } else if (result.smartLinkAccountExists) {
      setSmartLinkAccountExists(result.smartLinkAccountExists);
    } else {
      clearSignupOnboarding();
      setError(result.error || t("account.register.errorRegistrationFailed"));
    }
  };

  const token = accessToken ?? (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const handleResendEmail = async () => {
    if (!token) {
      setError(t("account.common.sessionExpired"));
      return;
    }
    setError(null);
    setResendLoading(true);
    try {
      await authApi.requestEmailVerification(token);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : t("account.register.errorResendFailed"));
    } finally {
      setResendLoading(false);
    }
  };

  const displayEmail = email || "your@email.com";

  return (
    <Card maxWidth={560} className={authText.authCardScroll}>
      <AuthBrand />

      {smartLinkAccountExists && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, textAlign: "center" }}>
          <h2 className={authText.titleMdCentered}>
            {tx("account.smartLink.accountExists.title")}
          </h2>
          <p className={authText.subtitle}>
            {tx("account.smartLink.accountExists.body")}
          </p>
          <Button
            color="primary"
            variant="solid"
            fullWidth
            size="lg"
            onClick={() => navigate({ to: "/login", search: { redirect: undefined } })}
          >
            {tx("account.smartLink.accountExists.goToSignIn")}
          </Button>
        </div>
      )}

      {!smartLinkAccountExists && step === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <h2 className={authText.titleMd}>{tx("account.register.title")}</h2>
          <p className={authText.subtitleSpaced}>{tx("account.register.subtitle")}</p>
          {error && <Callout variant="error">{error}</Callout>}
          <div className={authText.nameFieldsRow}>
            <div className={authText.nameField}>
              <TextInput
                label={t("account.register.firstName")}
                placeholder={t("account.register.firstNamePlaceholder")}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className={authText.nameField}>
              <TextInput
                label={t("account.register.lastName")}
                placeholder={t("account.register.lastNamePlaceholder")}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>
          <div style={{ height: 4 }} />
          <EmailInput
            label={t("account.register.workEmail")}
            placeholder={t("account.register.workEmailPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            hint={
              hasPendingInvite
                ? t("account.register.emailHintInvite")
                : t("account.register.emailHintDefault")
            }
          />
          <div style={{ height: 4 }} />
          <PasswordInput
            label={t("account.common.passwordLabel")}
            placeholder={t("account.register.passwordPlaceholder")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {password && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <PasswordStrengthBar pw={password} />
              <PwChecklist pw={password} />
            </div>
          )}
          <div style={{ height: 4 }} />
          <PasswordInput
            label={t("account.register.confirmPasswordLabel")}
            placeholder={t("account.register.confirmPasswordPlaceholder")}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <div style={{ height: 4 }} />
          <label className={authText.checkboxLabelStart}>
            <Checkbox id="terms" className={checkboxStyles.inLabel} checked={terms} onCheckedChange={setTerms} />
            <span className={authText.checkboxLabelText}>
              {tx("account.register.termsAgreePrefix")}{" "}
              <a href="#" className={authText.link} onClick={(e) => e.preventDefault()}>
                {tx("account.register.termsOfService")}
              </a>{" "}
              {tx("account.register.termsAnd")}{" "}
              <a href="#" className={authText.link} onClick={(e) => e.preventDefault()}>
                {tx("account.register.privacyPolicy")}
              </a>
            </span>
          </label>
          <div style={{ height: 8 }} />
          <Button color="primary" variant="solid" fullWidth size="lg" loading={loading} onClick={handleAccountSubmit}>
            {tx("account.register.continue")}
          </Button>
          <p className={authText.footerCompact}>
            {tx("account.register.alreadyHaveAccount")}{" "}
            <Link to="/login" className={authText.link}>
              {tx("account.register.signIn")}
            </Link>
          </p>
        </div>
      )}

      {step === 2 && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, textAlign: "center" }}>
          <div className={authText.stepIconCircle}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </div>
          <div>
            <h2 className={authText.titleMdCentered}>{tx("account.register.accountCreated")}</h2>
            <p className={authText.subtitle}>
              {tx("account.register.verifyEmailSentPrefix")}{" "}
              <strong className={authText.strong}>{displayEmail}</strong>
              {tx("account.register.verifyEmailSentSuffix")}
            </p>
          </div>
          {error && <Callout variant="error">{error}</Callout>}
          <EmailCodeEntry
            email={displayEmail}
            onVerify={(code) =>
              navigate({ to: "/user-setup", search: { email: displayEmail, code } })
            }
            expandAsCta
          />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <button
              type="button"
              className={authText.textButton}
              style={{ textDecoration: "underline" }}
              disabled={resendLoading}
              onClick={handleResendEmail}
            >
              {resendLoading ? t("account.common.sending") : t("account.register.resendEmail")}
            </button>
            <p className={authText.hint}>{tx("account.register.verifyCodeExpiry")}</p>
          </div>
          {import.meta.env.DEV && (
            <a
              href="/captured-emails"
              target="_blank"
              rel="noopener noreferrer"
              className={authText.link}
              style={{ fontSize: 12 }}
            >
              View local email captures
            </a>
          )}
          <button
            type="button"
            className={authText.skipStepLink}
            onClick={() => navigate({ to: "/user-setup", search: { tab: "phone" } })}
          >
            {tx("account.register.skipForNow")}
          </button>
        </div>
      )}
    </Card>
  );
}
